import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ArrowRight, Lock, Trophy, Info, AlertTriangle, ShieldAlert, ShieldCheck, PlayCircle, Download, FileText } from "lucide-react";
import VideoPlayer from "@/components/VideoPlayer";
import PracticeCases from "@/components/PracticeCases";
import LegalDisclaimer from "@/components/LegalDisclaimer";

function SectionBlock({ section }) {
  const t = section.type || "text";
  const image = section.image ? (
    <figure className="mb-6 rounded-md overflow-hidden border border-zinc-200">
      <img
        src={section.image}
        alt={section.image_alt || section.heading || ""}
        className="w-full h-48 md:h-56 object-cover"
        data-testid="section-image"
      />
    </figure>
  ) : null;

  if (t === "list") {
    return (
      <section>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3">{section.heading}</h2>
        {section.intro && <p className="text-zinc-700 mb-4">{section.intro}</p>}
        {image}
        <ul className="space-y-2 pl-0">
          {section.items.map((it, i) => (
            <li key={`li-${i}-${String(it).slice(0, 20)}`} className="flex gap-3 items-start">
              <span className="flex-shrink-0 mt-2 h-1.5 w-1.5 bg-red-600 rounded-full" />
              <span className="text-zinc-700">{it}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (t === "steps") {
    return (
      <section>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3">{section.heading}</h2>
        {section.intro && <p className="text-zinc-700 mb-5">{section.intro}</p>}
        {image}
        <ol className="space-y-3">
          {section.steps.map((s, i) => (
            <li key={`step-${i}-${String(s).slice(0, 20)}`} className="flex gap-4 items-start">
              <span className="flex-shrink-0 h-8 w-8 rounded-md bg-zinc-900 text-white font-mono text-sm flex items-center justify-center font-bold">
                {i + 1}
              </span>
              <span className="text-zinc-700 pt-1">{s}</span>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  if (t === "callout") {
    const styles = {
      info:    { bg: "bg-blue-50",    border: "border-blue-600",    text: "text-blue-900",    icon: Info,        ic: "text-blue-600" },
      warning: { bg: "bg-amber-50",   border: "border-amber-600",   text: "text-amber-900",   icon: AlertTriangle, ic: "text-amber-600" },
      danger:  { bg: "bg-red-50",     border: "border-red-600",     text: "text-red-900",     icon: ShieldAlert, ic: "text-red-600" },
      success: { bg: "bg-emerald-50", border: "border-emerald-600", text: "text-emerald-900", icon: ShieldCheck, ic: "text-emerald-600" },
    };
    const cfg = styles[section.variant] || styles.info;
    const Icon = cfg.icon;
    return (
      <section className={`${cfg.bg} border-l-4 ${cfg.border} rounded-r-md p-5 flex gap-4`} data-testid={`callout-${section.variant}`}>
        <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${cfg.ic}`} />
        <div>
          <div className={`font-display font-bold text-base mb-1 ${cfg.text}`}>{section.title}</div>
          <div className={`text-sm ${cfg.text} leading-relaxed`}>{section.body}</div>
        </div>
      </section>
    );
  }

  if (t === "stats") {
    return (
      <section>
        {section.heading && <h2 className="font-display text-2xl font-bold tracking-tight mb-5">{section.heading}</h2>}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-zinc-200">
          {section.items.map((s, i) => (
            <div key={`stat-${i}-${s.value}`} className="border-r border-b border-zinc-200 p-5 bg-white">
              <div className="font-display text-3xl font-black text-red-600 tracking-tight">{s.value}</div>
              <div className="text-xs text-zinc-600 mt-1.5 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // default: text
  return (
    <section>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-3">{section.heading}</h2>
      <p className="text-zinc-700">{section.body}</p>
    </section>
  );
}

export default function Course() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const isVideoWatched = !!(
    activeModule &&
    activeModule.video_url &&
    progress?.watched_modules?.includes(activeModule.slug)
  );

  const handleVideoEnded = async () => {
    if (!activeModule) return;
    try {
      const res = await api.post(`/progress/watched/${activeModule.slug}`);
      setProgress((p) => ({ ...(p || {}), watched_modules: res.data.watched_modules }));
      toast.success("Video complete — you can now mark this module complete.");
    } catch (err) {
      console.error("Failed to record watched progress", err);
    }
  };

  const downloadModulePdf = async () => {
    if (!activeModule || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/modules/${activeModule.slug}/pdf`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `bbp-module-${activeModule.slug}.pdf`;
      link.click();
      toast.success("Module PDF downloaded");
    } catch (err) {
      console.error("Module PDF download failed", err);
      toast.error("Could not download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    (async () => {
      const p = await api.get("/progress");
      const m = await api.get("/modules");
      setProgress(p.data);
      setModules(m.data.modules);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!slug) {
      setActiveModule(null);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/modules/${slug}`);
        setActiveModule(res.data);
        window.scrollTo({ top: 0 });
      } catch (err) {
        console.error("Failed to load module", err);
        toast.error("Module not found");
        navigate("/course");
      }
    })();
  }, [slug, navigate]);

  const markComplete = async () => {
    if (!activeModule) return;
    // Gate: if module has a video, require it to be watched first
    if (activeModule.video_url && !isVideoWatched) {
      toast.error("Please watch the video first to complete this module.");
      return;
    }
    const res = await api.post(`/progress/complete/${activeModule.slug}`);
    setProgress((p) => ({ ...p, completed_modules: res.data.completed_modules }));
    setModules((mods) => mods.map((m) => (m.slug === activeModule.slug ? { ...m, completed: true } : m)));
    toast.success("Module completed!");

    // Auto-advance
    const idx = modules.findIndex((m) => m.slug === activeModule.slug);
    const next = modules[idx + 1];
    if (next) {
      navigate(`/course/${next.slug}`);
    } else {
      navigate("/course");
      toast.success("All modules complete — ready for the exam!");
    }
  };

  const isCompleted = activeModule && progress?.completed_modules?.includes(activeModule.slug);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="p-8 text-sm text-zinc-500 font-mono">Loading course…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="lg:sticky lg:top-24 bg-white border-2 border-zinc-900 rounded-lg overflow-hidden">
            <div className="p-5 border-b border-zinc-900 bg-zinc-900 text-white">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-400">Course</div>
              <div className="font-display font-bold text-lg leading-tight mt-1">Bloodborne Pathogens Training</div>
              <div className="mt-3 flex justify-between items-center text-xs text-zinc-400 font-mono">
                <span>{progress?.completed_modules?.length || 0}/{progress?.total_modules} MODULES</span>
                <span>{progress?.percent || 0}%</span>
              </div>
              <div className="mt-2 h-1 bg-zinc-700 rounded-full overflow-hidden">
                <div className="h-full bg-red-600 transition-all" style={{ width: `${progress?.percent || 0}%` }} />
              </div>
            </div>

            <nav className="divide-y divide-zinc-100" data-testid="course-sidebar-nav">
              {modules.map((m, i) => {
                const active = m.slug === slug;
                return (
                  <Link
                    key={m.slug}
                    to={`/course/${m.slug}`}
                    data-testid={`sidebar-module-${m.slug}`}
                    className={`flex items-start gap-3 px-4 py-3 text-sm transition-colors ${
                      active ? "bg-red-50 border-l-4 border-red-600" : "border-l-4 border-transparent hover:bg-zinc-50"
                    }`}
                  >
                    <div className="font-mono text-xs text-zinc-500 pt-0.5 w-6">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1">
                      <div className={`font-medium leading-tight ${active ? "text-red-600" : ""}`}>{m.title}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{m.duration_min} min</div>
                    </div>
                    {m.completed && <CheckCircle2 className="h-4 w-4 text-red-600 flex-shrink-0 mt-1" />}
                  </Link>
                );
              })}
            </nav>

            <div className="border-t border-zinc-200 p-4">
              <Button
                onClick={() => navigate("/exam")}
                disabled={progress?.percent !== 100}
                className={`w-full ${progress?.percent === 100 ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}
                variant={progress?.percent === 100 ? "default" : "outline"}
                data-testid="sidebar-exam-btn"
              >
                {progress?.percent === 100 ? (
                  <><Trophy className="mr-2 h-4 w-4" /> Take final exam</>
                ) : (
                  <><Lock className="mr-2 h-4 w-4" /> Exam locked</>
                )}
              </Button>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="lg:col-span-8 xl:col-span-9 fade-in" data-testid="course-content">
          {!activeModule ? (
            <div className="bg-white border-2 border-zinc-900 rounded-lg p-12">
              <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">Course overview</div>
              <h1 className="font-display text-4xl font-bold tracking-tight">Bloodborne Pathogens Training</h1>
              <p className="text-zinc-700 mt-4 max-w-2xl leading-relaxed">
                A 13-module competency-based course with curricula aligned to OSHA 29 CFR 1910.1030 —
                statistics, procedures, and callouts covering everything from pathogen basics to
                exposure response. Successful completion earns an independent IMCAC competency
                certificate.
                Pick any module in the sidebar to begin, or start from the top.
              </p>
              <p className="mt-3 text-xs text-zinc-500 italic max-w-2xl">
                Note: this is a competency-based credential from IMCAC — an independent competency assessment and certification body.
                It does <strong>not</strong> replace, supersede, or constitute professional registration, licensure, or authorization to practice under the laws of any country.
              </p>
              <Button
                onClick={() => navigate(`/course/${modules[0].slug}`)}
                className="mt-8 bg-red-600 hover:bg-red-700 text-white"
                data-testid="start-first-module-btn"
              >
                Start module 1 <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ) : (
            <article className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              {activeModule.hero_image && (
                <div className="relative h-56 md:h-72 border-b border-zinc-200 overflow-hidden">
                  <img
                    src={activeModule.hero_image}
                    alt={activeModule.hero_alt || activeModule.title}
                    className="w-full h-full object-cover"
                    data-testid="module-hero-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
                  <div className="absolute bottom-4 left-8">
                    <div className="inline-block bg-red-600 text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-sm">
                      Module {activeModule.order}
                    </div>
                  </div>
                </div>
              )}

              {activeModule.interactive === "practice_cases" ? (
                <div className="p-8 lg:p-12">
                  <PracticeCases />
                </div>
              ) : (
              <div className="p-8 lg:p-12">
                {!activeModule.hero_image && (
                  <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">
                    Module {activeModule.order} · {activeModule.duration_min} min read
                  </div>
                )}
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">{activeModule.title}</h1>
                    <p className="text-zinc-600 mt-3 text-lg">{activeModule.subtitle}</p>
                    <div className="mt-2 text-xs text-zinc-500 font-mono">{activeModule.duration_min} min read</div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadModulePdf}
                    className="mt-1 border-zinc-900 hover:bg-zinc-50"
                    data-testid="download-module-pdf-btn"
                    disabled={downloadingPdf}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {downloadingPdf ? "Preparing…" : "Download PDF"}
                  </Button>
                </div>

                {activeModule.video_url && (
                  <div className="mt-8" data-testid="module-video-section">
                    <div className="flex items-center gap-2 mb-3">
                      <PlayCircle className="h-5 w-5 text-red-600" />
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-700">
                        Watch: {activeModule.video_title || "Demonstration video"}
                      </div>
                      {isVideoWatched && (
                        <span className="ml-auto inline-flex items-center gap-1 text-xs font-mono text-emerald-700" data-testid="video-watched-badge">
                          <CheckCircle2 className="h-3.5 w-3.5" /> WATCHED
                        </span>
                      )}
                    </div>
                    <VideoPlayer
                      src={activeModule.video_url}
                      title={activeModule.video_title || activeModule.title}
                      onWatched={handleVideoEnded}
                    />
                    <p className="mt-2 text-xs text-zinc-500 font-mono">
                      Captions available via the CC button in the player. Marks as watched after 90% viewed. Transcript key points below.
                    </p>

                    {activeModule.video_key_points?.length > 0 && (
                      <div className="mt-4 border border-zinc-200 rounded-md bg-zinc-50" data-testid="video-transcript-panel">
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-white">
                          <FileText className="h-4 w-4 text-zinc-700" />
                          <div className="text-xs font-mono uppercase tracking-widest text-zinc-700">
                            Transcript — key points
                          </div>
                        </div>
                        <ul className="p-4 space-y-2">
                          {activeModule.video_key_points.map((kp, i) => (
                            <li key={`kp-${i}-${String(kp).slice(0, 20)}`} className="flex gap-3 items-start text-sm">
                              <span className="flex-shrink-0 h-1.5 w-1.5 mt-2 rounded-full bg-red-600" />
                              <span className="text-zinc-700 leading-relaxed">{kp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-10 max-w-3xl prose-course space-y-10" data-testid="module-sections">
                  {activeModule.sections.map((s, i) => (
                    <SectionBlock key={`section-${i}-${s.heading || s.title || s.type}`} section={s} />
                  ))}
                </div>

              <div className="mt-14 pt-8 border-t border-zinc-200 flex flex-wrap gap-3 items-center justify-between">
                <div className="flex gap-2">
                  {activeModule.order > 1 && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/course/${modules[activeModule.order - 2].slug}`)}
                      data-testid="prev-module-btn"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> Previous
                    </Button>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {activeModule.video_url && !isVideoWatched && (
                    <span className="text-xs text-zinc-500 font-mono mr-2" data-testid="watch-required-hint">
                      Watch the video to unlock →
                    </span>
                  )}
                  <Button
                    onClick={markComplete}
                    disabled={activeModule.video_url && !isVideoWatched && !isCompleted}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:bg-zinc-300 disabled:text-zinc-500 disabled:cursor-not-allowed"
                    data-testid="mark-complete-btn"
                  >
                    {activeModule.video_url && !isVideoWatched && !isCompleted && (
                      <Lock className="mr-2 h-4 w-4" />
                    )}
                    {isCompleted ? "Continue" : "Mark complete"} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
              </div>
              )}
            </article>
          )}
        </main>
      </div>

      <footer className="mt-12 bg-zinc-900 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <LegalDisclaimer variant="footer" />
        </div>
      </footer>
    </div>
  );
}
